import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, unwrapApiResponse } from '@/utils/api';
import FormatoPicker, { FORMATO_ID_HENTAI } from '@/components/FormatoPicker';
import ListCardGrid from '@/components/lists/ListCardGrid';
import { publicListaToListCard } from '@/components/lists/listCardUtils';
import type { ListCard } from '@/components/lists/types';
import { List } from 'lucide-react';

/** Sem hentai (10) — alinhado ao picker do perfil público. */
const FORMATO_IDS_VALIDOS = new Set([1, 3, 4, 5, 8, 9, 13]);
const FORMATO_PADRAO = 5;

interface PublicListaApi {
    id: number;
    nome: string;
    descricao?: string | null;
    publica: boolean;
    criada_em?: string;
    total_obras: number;
    total_seguidores: number;
    usuario?: { id?: number; nome?: string; nick: string; imagem?: string | null };
    obras?: { id?: number; obr_id?: number; nome?: string; imagem?: string | null; capa?: string | null }[];
}

function normalizeListasPayload(raw: unknown): PublicListaApi[] {
    if (Array.isArray(raw)) return raw as PublicListaApi[];
    if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
        return (raw as { data: PublicListaApi[] }).data;
    }
    return [];
}

export function PerfilListasPublicasTab({
    profileUsrId,
    profileNick,
}: {
    profileUsrId: number;
    profileNick: string;
}) {
    const navigate = useNavigate();
    const [formatoId, setFormatoId] = useState(FORMATO_PADRAO);
    useEffect(() => {
        if (formatoId === FORMATO_ID_HENTAI) setFormatoId(FORMATO_PADRAO);
    }, [formatoId]);
    const [listas, setListas] = useState<PublicListaApi[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchListas = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('ordenar', 'seguidores_desc');
            params.set('formato', String(formatoId));
            const nick = profileNick.trim();
            if (nick) params.set('nick', nick);
            const res = await api.get(`listas/publicas?${params}`, { requiresAuth: true });
            const raw = unwrapApiResponse<unknown>(res);
            const all = normalizeListasPayload(raw);
            const mine = all.filter((l) => {
                const uid = l.usuario?.id;
                if (uid != null && uid === profileUsrId) return true;
                const n = (l.usuario?.nick ?? '').trim().toLowerCase();
                return n === nick.toLowerCase();
            });
            setListas(mine);
        } catch {
            setListas([]);
        } finally {
            setLoading(false);
        }
    }, [formatoId, profileNick, profileUsrId]);

    useEffect(() => {
        void fetchListas();
    }, [fetchListas]);

    const cards: ListCard[] = useMemo(
        () => listas.map((l) => publicListaToListCard(l)),
        [listas],
    );

    const onOpenList = (id: string) => {
        navigate(`/listas/${id}`);
    };

    const onSelectFormato = (id: number) => {
        if (!FORMATO_IDS_VALIDOS.has(id)) return;
        setFormatoId(id);
    };

    return (
        <div className="text-white">

            <div className="mb-8">
                <FormatoPicker
                    onSelect={onSelectFormato}
                    selectedFormato={formatoId}
                    excludeFormatIds={[FORMATO_ID_HENTAI]}
                />
            </div>

            {loading ? (
                <div className="flex flex-col items-center py-20">
                    <div className="w-10 h-10 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mb-4" />
                    <p className="text-gray-400 text-sm">A carregar listas…</p>
                </div>
            ) : cards.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-white/10 bg-white/[0.03]">
                    <List className="w-16 h-16 text-white/15 mb-4" />
                    <p className="text-white/45 text-center px-4">
                        Nenhuma lista pública com obras neste formato.
                    </p>
                </div>
            ) : (
                <>
                    <p className="text-sm text-white/55 mb-6">
                        <span className="text-white font-bold tabular-nums">{listas.length}</span>{' '}
                        {listas.length === 1 ? 'lista' : 'listas'}
                    </p>
                    <ListCardGrid lists={cards} onOpenList={onOpenList} />
                </>
            )}
        </div>
    );
}
